import React, { useContext, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { RestaurantConfirmedButton } from './RestaurantConfirmedButton';
import { getDeliveryTime, isRestaurantOpenForDeliveryType, linkToRestaurantPage } from './helpers';
import { mediaQuery, typography } from '../../../../theme';
import Done from '@mui/icons-material/Done';
import { AddressSelectionData } from '../../types';
import { OrderType } from '../../../../types';
import { AddressSelectionContext } from '../../AddressSelectionProvider';
import { Currency } from '../../../shoppingCart/components/orderItem/Currency';
import { useStory } from '../../../storyblok/hooks/useStoryblok';
import { mapColorNameToColor } from '../../../utils/storyblokHelpers';
import { useDeliveryCampaign } from './hooks/useDeliveryCampaign';
import { useShownServiceFee } from './hooks/useShownServiceFee';
import LinkNoPrefetch from '../../../linkNoPrefetch/LinkNoPrefetch';
import { distance } from '@turf/distance';
import { hiddenRestaurants } from '../../../restaurants/hooks/useRestaurants';
import { DeliveringRestaurantDto } from '@kotipizzagroup/kotipizza-shops-api-client';
import { colors } from '../../../../theme/color';
import { useServiceFeeViewEvent } from '../../../../hooks/useServiceFeeViewEvent';
import { AnalyticsServiceFeeLocation } from '../../../../utils/analytics/analytics';

export type RestaurantRowProps = {
  active: boolean;
  restaurant: DeliveringRestaurantDto;
};

const RestaurantRow: React.FC<RestaurantRowProps> = ({ active, restaurant }) => {
  const { data: addressSelectionData } = useStory<AddressSelectionData>(
    'address-selection',
    'addressSelection'
  );
  const color = mapColorNameToColor(addressSelectionData?.price_color);
  const { campaignDeliveryFee, useCampaignPrice } = useDeliveryCampaign({
    restaurant,
    isPreorder: false,
  });

  const {
    displayName,
    restaurantId,
    dynamicDeliveryFee,
    deliveryFee,
    deliveryRedirectionRestaurant,
    serviceFee,
  } = restaurant;

  const shownServiceFee = useShownServiceFee(serviceFee);

  const { setRestaurantId, orderType, userLocation } = useContext(AddressSelectionContext);

  const isDelivery: boolean = orderType === OrderType.DELIVERY;

  const distanceText = useMemo(() => {
    if (!userLocation) return null;

    const meters = distance(
      [userLocation.longitude || 0, userLocation.latitude || 0],
      [restaurant.longitude || 0, restaurant.latitude || 0],
      { units: 'meters' }
    );
    return getFormattedDistance(meters);
  }, [userLocation, restaurant.latitude, restaurant.longitude]);

  const currentDeliveryFee =
    dynamicDeliveryFee !== undefined && dynamicDeliveryFee !== null
      ? dynamicDeliveryFee
      : deliveryFee;

  const { isOpen, restaurantStatus, restaurantIsRedirected, currentEstimate } =
    isRestaurantOpenForDeliveryType(restaurant, orderType);

  // The fee row below is rendered only for an open restaurant, so the impression
  // follows the same gate.
  useServiceFeeViewEvent({
    isRendered: isOpen,
    location: AnalyticsServiceFeeLocation.AddressSelection,
    serviceFee: shownServiceFee,
    orderType,
    restaurantId,
  });

  const chooseRestaurant = (): void => {
    if (isOpen) {
      setRestaurantId(restaurantId);
    }
  };

  const isRedirectionInfoVisible =
    restaurantIsRedirected &&
    deliveryRedirectionRestaurant &&
    !hiddenRestaurants.includes(deliveryRedirectionRestaurant.externalId);

  return (
    <RestaurantRowWrapper
      data-testid={`restaurant-row-${restaurantId}`}
      data-isopen={isOpen}
      active={active && isOpen}
      online={isOpen}
    >
      <RestaurantRowButton aria-selected={active && isOpen} onClick={chooseRestaurant}>
        <RowUpper>
          <div>
            <RestaurantTitle redirected={false}>
              {active && isDelivery && isRedirectionInfoVisible && <span>Ravintolan </span>}
              <span translate="no">{displayName}</span>
              {active && isDelivery && isRedirectionInfoVisible && (
                <span> tilaukset paistaa ja kuljettaa tällä hetkellä </span>
              )}
              {active &&
                isDelivery &&
                isRedirectionInfoVisible &&
                deliveryRedirectionRestaurant?.displayName}
            </RestaurantTitle>
          </div>
          <IconActive active={active && isOpen} aria-hidden="true">
            <Done htmlColor="white" fontSize="inherit" />
          </IconActive>
          {distanceText && <DistanceLabel>{distanceText}</DistanceLabel>}
        </RowUpper>
        <RowBottom>
          <RestaurantRowTime
            text={getDeliveryTime(orderType, restaurantStatus, currentEstimate)}
            restaurantDisplayName={displayName}
            showLinkToOpeningHours={!isOpen}
          />
          {isOpen && (
            <DeliveryFeeBlock>
              {shownServiceFee > 0 && (
                <ServiceFeePrice data-testid="service-fee">
                  <span>Palvelumaksu </span>
                  <Currency>{shownServiceFee}</Currency>
                </ServiceFeePrice>
              )}
              {isDelivery && (
                <>
                  <DeliveryPrice isCampaignPrice={useCampaignPrice}>
                    <DeliveryFeeText>Kuljetushinta nyt</DeliveryFeeText>
                    <DeliveryFeePrice color={dynamicDeliveryFee ? color : colors.textDefault}>
                      <Currency>
                        {useCampaignPrice ? campaignDeliveryFee : currentDeliveryFee}
                      </Currency>
                    </DeliveryFeePrice>
                  </DeliveryPrice>
                  {Boolean(currentDeliveryFee && useCampaignPrice) && (
                    <DisabledPrice aria-hidden="true">
                      <Currency prefix="norm. ">{currentDeliveryFee}</Currency>
                    </DisabledPrice>
                  )}
                </>
              )}
            </DeliveryFeeBlock>
          )}
        </RowBottom>
      </RestaurantRowButton>
      <RestaurantConfirmedButton disabled={!isOpen || !active} />
    </RestaurantRowWrapper>
  );
};

type RestaurantRowTimeProps = {
  text: string;
  restaurantDisplayName: string;
  showLinkToOpeningHours: boolean;
};

export const RestaurantRowTime: React.FC<RestaurantRowTimeProps> = ({
  text,
  restaurantDisplayName,
  showLinkToOpeningHours,
}) => {
  return (
    <Time>
      <TimeText>
        {text}
        {showLinkToOpeningHours && (
          <HoursLink href={linkToRestaurantPage(restaurantDisplayName)}>
            {' ' + 'Aukioloajat'}
          </HoursLink>
        )}
      </TimeText>
    </Time>
  );
};

const toLocaleDecimal = (amount: number): string =>
  amount.toLocaleString('fi-FI', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const roundUpToNearestHundred = (num: number): number => Math.ceil(num / 100) * 100;

const getFormattedDistance = (meters: number): string => {
  if (meters < 500) return `alle 500 m päässä`;
  if (meters < 1000) return `n. ${roundUpToNearestHundred(meters)} m päässä`;
  return `n. ${toLocaleDecimal(roundUpToNearestHundred(meters) / 1000)} km päässä`;
};

export const RestaurantRowWrapper = styled.div<{
  online: boolean;
  active: boolean;
}>(
  ({ online, active }) => css`
    border: 1px solid ${colors.borderDefault};
    margin-bottom: 0;
    width: 100%;
    border-radius: initial;
    opacity: ${online ? 1 : 0.5};
    box-shadow: none;
    cursor: ${online ? 'pointer' : 'initial'};
    background: ${active ? colors.green.lighter(90) : 'transparent'};
    color: black;
    position: relative;

    &:not(:last-child) {
      margin-bottom: 0;
      border-bottom: 1px transparent solid;
    }

    &:first-child {
      border-top-left-radius: ${({ theme }) => theme.sizes?.borderRadius.default};
      border-top-right-radius: ${({ theme }) => theme.sizes?.borderRadius.default};
    }

    &:last-child {
      border-bottom-left-radius: ${({ theme }) => theme.sizes?.borderRadius.default};
      border-bottom-right-radius: ${({ theme }) => theme.sizes?.borderRadius.default};
    }

    &:hover {
      border-bottom: 1px solid ${colors.borderDefault};
      border-color: ${colors.outlineDefault};
    }

    &:focus-within:has(:focus-visible) {
      outline: 3px solid ${colors.outlineDefault};
    }
  `
);

export const RestaurantRowButton = styled.button`
  all: unset;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding: 16px 24px 8px 24px;
  width: 100%;
  color: black;

  &:focus {
    outline: 0;
  }
  @media ${mediaQuery.tiny} {
    padding: 16px 24px 8px 24px;
  }
`;

export const RowUpper = styled.div`
  position: relative;
  margin-bottom: 5px;
`;

export const RestaurantTitle = styled.span<{ redirected: boolean }>`
  ${typography.titleStyle}
  text-align: left;
  letter-spacing: 0.18px;
  margin-right: 20px;
  color: ${colors.brandPrimary};

  ${(props) =>
    props.redirected &&
    `
      color: ${colors.gray50};
    `}

  @media ${mediaQuery.medium} {
    max-width: 80%;
    display: inline-block;
  }
`;

export const RedirectedRestaurantText = styled.div`
  font-size: 14px;
  line-height: 125%;
  letter-spacing: 0.18px;

  @media ${mediaQuery.medium} {
    max-width: 80%;
    display: inline-block;
  }
`;

export const RedirectedRestaurantTitle = styled.div`
  display: inline-block;
  font-weight: 600;
  color: ${colors.brandPrimary};
`;

export const RowBottom = styled.div`
  display: flex;
  justify-content: space-between;
  line-height: 125%;
`;

export const IconActive = styled.span<{ active: boolean }>`
  display: none;
  height: 24px;
  width: 24px;
  font-size: 24px;
  background: ${colors.brandPrimary};
  border-radius: 50%;
  position: absolute;
  right: 0;
  top: 0;

  ${(props) =>
    props.active &&
    `
      display: block;
    `};
`;

export const DeliveryFeeBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-basis: 100%;
  flex-grow: 1;
  margin-left: 8px;
`;

const DeliveryPrice = styled.div<{ isCampaignPrice?: boolean }>`
  display: flex;

  ${({ isCampaignPrice }) =>
    isCampaignPrice &&
    css`
      border-radius: ${({ theme }) => theme.sizes?.borderRadius.default};
      background: ${colors.yellow.base};
      padding: 4px 8px;
    `}
`;

export const Time = styled.span`
  flex-basis: 50%;
  flex-shrink: 1;
  flex-grow: 1;
`;

export const TimeText = styled.span`
  margin-left: 0;
  position: relative;
  font-size: 16px;
  top: -2px;

  @media ${mediaQuery.tiny} {
    font-size: 13px;
    margin-left: 3px;
  }
`;

export const HoursLink = styled(LinkNoPrefetch)`
  margin-left: 10px;
  text-decoration: underline;
  color: ${colors.black};
`;

export const DeliveryFeeText = styled.div`
  text-align: right;

  @media ${mediaQuery.tiny} {
    font-size: ${typography.fontSize.SMALL};
  }
`;

export const DisabledPrice = styled.div`
  ${typography.textSmallStyle}
  color: ${colors.textDisabled};
  text-decoration: line-through;
  display: flex;
  margin-top: 4px;
`;

export const ServiceFeePrice = styled.div`
  ${typography.textSmallStyle}
  color: ${colors.gray60};
  display: flex;
  margin-top: 4px;

  span {
    margin-right: 4px;
  }
`;

export const DeliveryFeePrice = styled.div<{ color: string; isPreorder?: boolean }>`
  margin-left: 4px;
  font-weight: 700;
  text-align: right;
  display: inline-flex;
  flex-direction: ${({ isPreorder }) => (isPreorder ? 'row' : 'column')};
  align-items: center;

  ${({ color }) => css`
    font-weight: 600;
    color: ${color};
  `}

  @media ${mediaQuery.small} {
    flex-direction: ${({ isPreorder }) => (isPreorder ? 'row' : 'column')};
  }

  @media ${mediaQuery.tiny} {
    font-size: ${typography.fontSize.SMALL};
    margin-left: 4px;
  }
`;

export const DistanceLabel = styled.label`
  ${typography.textSmallStyle}
  color: ${colors.gray90};
`;

export default RestaurantRow;
